import React, { useState } from 'react';
import { Instagram, Facebook, Twitter, Youtube, Mail, MapPin, Phone } from 'lucide-react';
import ContactModal from './ContactModal';

const IDEALAGE_LOGO = 'https://customer-assets.emergentagent.com/job_902719ec-3f1d-441e-9a91-2c280a563b23/artifacts/ie24qqvd_White-Idealage-Logo-768x241.png';

const Footer = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  const collections = [
    { name: 'Fitness', href: '#fitness' },
    { name: 'Anti-Aging', href: '#anti-aging' },
    { name: 'Mind & Mood', href: '#mind-mood' },
    { name: 'Immune Support', href: '#immune-support' },
  ];

  const quickLinks = [
    { name: 'About Us', href: 'https://www.mdlogichealth.com/pages/about-us' },
    { name: 'FAQs', href: 'https://www.mdlogichealth.com/pages/shipping-returns' },
    { name: 'Contact Us', href: '#', isContact: true },
    { name: 'Shipping & Returns', href: 'https://www.mdlogichealth.com/pages/shipping-returns' },
  ];

  const legal = [
    { name: 'Privacy Policy', href: 'https://www.mdlogichealth.com/policies/privacy-policy' },
    { name: 'Terms of Service', href: 'https://www.mdlogichealth.com/policies/terms-of-service' },
    { name: 'Subscription Policy', href: 'https://www.mdlogichealth.com/pages/subscription-policy' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://instagram.com/mdlogichealth', label: 'Instagram' },
    { icon: Facebook, href: 'https://facebook.com/mdlogichealth', label: 'Facebook' },
    { icon: Twitter, href: 'https://x.com/mdlogichealth', label: 'Twitter' },
    { icon: Youtube, href: 'https://www.youtube.com/MDLogicHealth', label: 'YouTube' },
  ];

  return (
    <footer className="bg-stone-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="mb-4">
              <img 
                src={IDEALAGE_LOGO} 
                alt="Ideal Age" 
                className="h-10 w-auto"
              />
              <p className="text-xs text-white/50 mt-2">Powered by MD Logic Health®</p>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Dedicated to supplementing your daily nutrition to help you live your best life. 
              All products undergo rigorous testing for heavy metals, molds, toxins, and GMP standard tests.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <social.icon className="w-4 h-4 text-white/60" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium uppercase tracking-wider text-white/80 mb-6">
              Protocols
            </h4>
            <ul className="space-y-3">
              {collections.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className="text-sm text-white/50 hover:text-white transition-colors"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium uppercase tracking-wider text-white/80 mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  {item.isContact ? (
                    <button
                      onClick={() => setContactModalOpen(true)}
                      className="text-sm text-white/50 hover:text-white transition-colors"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <a
                      href={item.href}
                      className="text-sm text-white/50 hover:text-white transition-colors"
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium uppercase tracking-wider text-white/80 mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-white/40 mt-0.5" />
                <span className="text-sm text-white/50">cs@mdlogichealth.com</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-white/40 mt-0.5" />
                <span className="text-sm text-white/50">1-877-629-8711</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-white/40 mt-0.5" />
                <span className="text-sm text-white/50">United States</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* FDA Disclaimer in faint box */}
          <div className="flex justify-center mb-6">
            <div className="border border-white/20 rounded-lg px-6 py-3">
              <p className="text-xs text-white/40 text-center leading-relaxed">
                *These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">
              © {currentYear} MD Logic Health®. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {legal.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-xs text-white/40 hover:text-white/60 transition-colors"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={contactModalOpen} 
        onClose={() => setContactModalOpen(false)} 
      />
    </footer>
  );
};

export default Footer;
